/**
 * Module download types for the GDB Cloud Edition.
 *
 * These types match the Rust server's JSON response shape from
 * GET /api/modules/:id/download (see server/src/server/handlers/platform/modules.rs).
 */

import { z } from 'zod'

/**
 * One available-update entry, as emitted by GDBCore.GetAvailableUpdates.
 *
 * GitLab #312 (source-tier notify-only updater): the C++ side resolves the
 * running plugin's tier (a buildable `Source/` folder next to the .uplugin →
 * "source", else "compiled") and adds three fields per entry:
 *
 *   - `buildType`          — the resolved tier ("compiled" | "source"),
 *                            echoed from the cloud's build_type_filter.
 *   - `isSourceTier`       — true when this install is a source build, which
 *                            must NEVER be auto-swapped by the updater (a
 *                            compiled bundle would clobber a source checkout).
 *   - `accountDownloadUrl` — the account downloads deep-link the bell points
 *                            at for a source build (host resolved by C++ from
 *                            gdb.ini/env, never hardcoded prod). Empty for
 *                            compiled builds.
 *
 * Older Cores omit these, so they are optional with safe defaults applied by
 * the Zod parser (`isSourceTier` → false, `accountDownloadUrl` → '').
 */
export const availableUpdateSchema = z.object({
  productId: z.string(),
  name: z.string(),
  currentVersion: z.string(),
  latestVersion: z.string(),
  changelog: z.string().optional(),
  /** Presigned download URL for the update archive. Optional — the C++ side holds it. */
  downloadUrl: z.string().optional(),
  /** Resolved build tier this entry targets ("compiled" | "source"). */
  buildType: z.string().optional(),
  /** True when the running install is a source build → notify-only, never auto-swap. */
  isSourceTier: z.boolean().default(false),
  /** True for the plugin-level "core" row — the Store's 'gamedevbuddy' OR a
   *  standalone bundle's own product id. The bell always shows this row (it's
   *  the plugin updating itself), independent of the entitlement list. */
  isCore: z.boolean().default(false),
  /** Account downloads deep-link for a source re-download. Empty for compiled. */
  accountDownloadUrl: z.string().default(''),
})

export type AvailableUpdate = z.infer<typeof availableUpdateSchema>

/**
 * Parse a GDBCore.GetAvailableUpdates response. C++ returns either a parsed
 * array or a JSON string; entries failing the schema are dropped rather than
 * poisoning the whole list (a forward-compat Core could add fields).
 */
export function parseAvailableUpdates(raw: unknown): AvailableUpdate[] {
  let input: unknown = raw
  if (typeof raw === 'string') {
    try {
      input = JSON.parse(raw)
    } catch {
      return []
    }
  }
  if (!Array.isArray(input)) return []
  const out: AvailableUpdate[] = []
  for (const item of input) {
    const result = availableUpdateSchema.safeParse(item)
    if (result.success) out.push(result.data)
  }
  return out
}

/** Download info for a single module version returned by the server. */
export interface ModuleDownloadInfo {
  id: string
  name: string
  version: string
  ueVersion: string
  /** Presigned R2 URL, valid for 5 minutes. */
  downloadUrl: string
  fileSize: number
  fileName: string
  releaseDate: string
}

/** Wrapper for GET /api/modules/:id/download response. */
export interface ModuleDownloadResponse {
  success: boolean
  module: ModuleDownloadInfo
}
